import { MdExitToApp, MdOutlineAddChart, MdOutlineLibraryBooks, MdOutlineFax, MdDeveloperMode } from "react-icons/md";
import logoPixi from "../../assets/logo-dixi.png";
import logoEvo from "../../assets/logo_evo.png";
import logoTecno from "../../assets/logo-tecno.png";
import { useNavigate, useLocation } from "react-router-dom";
import MenuService from "../../services/MenuService";
import "./MenuLateral.css";
import { useRef, useState, forwardRef, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { APP_ROUTES } from "../../services/app_routes";
import { isDev, validarPermissoes, PERMISSOES } from "../../utils/PermissaoUtils";
import UserProfileIcon from "../UserProfileIcon/UserProfileIcon";
import { useThemeController } from "../../hooks/ThemeHooks";

const MenuLateral = () => {

  const navigate = useNavigate();
  const { logout, user } = useAuth(); 
  const location = useLocation();
  const { isDixi, isEvo, isTecnoponto, temaAtual, TEMAS } = useThemeController();
  
  const [logoAtual, setLogoAtual] = useState(logoPixi);
  
  useEffect(() => {
    if (temaAtual === TEMAS.EVO) {
      setLogoAtual(logoEvo);
    } else if (temaAtual === TEMAS.TECNOPONTO) {
      setLogoAtual(logoTecno);
    } else {
      setLogoAtual(logoPixi);
    }
  }, [temaAtual, TEMAS.EVO, TEMAS.TECNOPONTO]); 
  
  const [menuAtivo, setMenuAtivo] = useState(null);
  const [submenuAtivo, setSubmenuAtivo] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0 });
  const [menuFechando, setMenuFechando] = useState(false);
  const cadastroRef = useRef(null);
  const equipamentoRef = useRef(null);
  const relatorioRef = useRef(null);

  
  const animationDuration = 300;

  const cadastroModules = MenuService.getCadastroModules();
  const equipamentoModules = MenuService.getEquipamentoModules();
  const relatorioModules = MenuService.getRelatorioModules();

  const isCadastro = () => {
    const first = location.pathname.split('/')[1];
    return cadastroModules.has(first);
  };

  const isEquipamento = () => {
    return equipamentoModules.has(location.pathname.split('/')[1]);
  };

  const isRelatorio = () => {
    return relatorioModules.has(location.pathname.split('/')[1]);
  };

  

  const defineMenuRef = (menu) => {
    switch (menu) {
      case "cadastro":
        return cadastroRef;
      case "equipamento":
        return equipamentoRef;
      case "relatorio":
        return relatorioRef;
      default:
        return null;
    }
  };

  const toggleMenu = (menu) => {
    if (menuAtivo === menu) {
      setMenuFechando(true);
      setTimeout(() => {
        setMenuAtivo(null);
        setMenuFechando(false);
      }, animationDuration); 
    } else {
      setMenuAtivo(menu);
      const ref = defineMenuRef(menu);
      if (ref.current) {
        const menuRect = ref.current.getBoundingClientRect();
        setSubmenuPosition({
          top: menuRect.top,
        });
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuAtivo && 
          !event.target.closest('.menu-lateral') && 
          !event.target.closest('.submenu')) {
        setMenuFechando(true);
        setTimeout(() => {
          setMenuAtivo(null);
          setMenuFechando(false);
        }, animationDuration);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuAtivo, animationDuration]);

  useEffect(() => {
    if (location.pathname === '/' || location.pathname === APP_ROUTES.HOME) {
      setSubmenuAtivo(null);
    }
  }, [location.pathname]);

  const submenus = MenuService.getAllSubmenus();

  const handleSubmenuClick = (rota, itemId) => {
    navigate(rota);
    setSubmenuAtivo(itemId);
    
    setMenuFechando(true);
    setTimeout(() => {
      setMenuAtivo(null);
      setMenuFechando(false);
    }, animationDuration);
  };

  const podeVerRelatorio = validarPermissoes(PERMISSOES.EMPRESA_LISTAR);
  const isUserDev = isDev();

  return (
    <div className="menu-container">
      <menu className={"menu-lateral"}>
        <img 
          key={`logo-${temaAtual}`}
          src={logoAtual} 
          className={"logo-menu-lateral"} 
          alt={"logo-menu"} 
          onClick={() => navigate(APP_ROUTES.HOME)} 
        />

        <ul className={"menu-lateral-itens"}>
          <ItemMenu
            ref={cadastroRef}
            icone={<MdOutlineLibraryBooks />}
            subtitulo={"Cadastro"}
            onClick={() => toggleMenu("cadastro")}
            ativo={menuAtivo === "cadastro"}
            outlined={isCadastro()}
          />

          <ItemMenu
            ref={equipamentoRef}
            icone={<MdOutlineFax />}
            subtitulo={"Equipamento"}
            onClick={() => toggleMenu("equipamento")}
            ativo={menuAtivo === "equipamento"}
            outlined={isEquipamento()}
          />

          {/* Exibe o menu Relatório apenas se o usuário tiver permissão EMPRESA_LISTAR */}
          {podeVerRelatorio && (
            <ItemMenu 
              ref={relatorioRef}
              icone={<MdOutlineAddChart />} 
              subtitulo={"Relatório"} 
              onClick={() => toggleMenu("relatorio")}
              ativo={menuAtivo === "relatorio"}
              outlined={isRelatorio()}
            />
          )}
          
        </ul>

        <ul className={"menu-lateral-rodape"}>
          <UserProfileIcon user={user} />
          <ItemMenuRodape 
            icone={<MdExitToApp />} 
            classCustom={"icone-sair"} 
            onClick={() => {
              logout();
            }}
          />
        </ul>
      </menu>
      
      {(menuAtivo || menuFechando) && (
        <SubMenu 
          style={submenuPosition} 
          className={menuFechando ? "submenu-fechando" : ""}
        >
          {submenus[menuAtivo]?.filter(item => !item.somenteDev || isUserDev).map((item) => (
            <li
              key={item.id}
              className={`submenu-item ${submenuAtivo === item.id ? "submenu-item-ativo" : ""}`}
              onClick={() => handleSubmenuClick(item.rota, item.id)}
            >
              {item.nome}
            </li>
          ))}
        </SubMenu>
      )}
    </div>
  );
};

const ItemMenu = forwardRef(({ icone, classCustom, subtitulo, onClick, ativo, outlined }, ref) => {
  return (
    <li 
      className={`item-menu ${classCustom || ""} ${ativo ? "item-menu-ativo" : ""} ${outlined && !ativo ? "item-menu-outlined" : ""}`} 
      onClick={onClick}
      ref={ref}
    >
      {icone}
      {subtitulo && <span className="subtitulo">{subtitulo}</span>}
    </li>
  );
});

const ItemMenuRodape = ({ icone, classCustom, onClick }) => {
  return (
    <li 
      className={`item-menu-rodape ${classCustom || ""}`}
      onClick={onClick}
    >
      {icone}
    </li>
  );
};

const SubMenu = ({ children, style, className }) => {
  return <div className={`submenu ${className || ""}`} style={style}>{children}</div>;
};

export default MenuLateral;
